import { useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { UNSAFE_DataRouterContext, useBlocker, useLocation, useNavigate } from "react-router-dom";
import { isTeamWorkspacePath } from "@/lib/team-workspace-routes";

import { WorkspaceNavigationContext, type WorkspaceNavigationGuard as Guard } from "./use-team-workspace-navigation";

function HistoryGuard({ hasGuards, requestNavigation }: { hasGuards: () => boolean; requestNavigation: (proceed: () => void, cancel?: () => void) => void }) {
  const blocker = useBlocker(({ historyAction }) => historyAction === "POP" && hasGuards());
  useEffect(() => {
    if (blocker.state === "blocked") requestNavigation(blocker.proceed, blocker.reset);
  }, [blocker, requestNavigation]);
  return null;
}

/** Editors keep ownership of their save/discard dialogs; the shared frame asks before leaving. */
export function TeamWorkspaceNavigation({ children }: { children: ReactNode }) {
  const guards = useRef(new Set<Guard>());
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const navigate = useNavigate();
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  const workspaceOpen = isTeamWorkspacePath(useLocation().pathname);
  const hasGuards = useCallback(() => guards.current.size > 0, []);
  const registerGuard = useCallback((guard: Guard) => {
    guards.current.add(guard);
    return () => { guards.current.delete(guard); };
  }, []);
  const requestNavigation = useCallback((proceed: () => void, cancel: () => void = () => undefined) => {
    const registered = [...guards.current];
    const next = (index: number) => {
      if (!alive.current) return;
      if (index === registered.length) proceed();
      else registered[index](() => next(index + 1), cancel);
    };
    next(0);
  }, []);
  const value = useMemo(() => ({ requestNavigation, registerGuard }), [requestNavigation, registerGuard]);
  return <WorkspaceNavigationContext.Provider value={value}>
    {dataRouter && workspaceOpen && <HistoryGuard hasGuards={hasGuards} requestNavigation={requestNavigation} />}
    <div className="contents" onClickCapture={(event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    const target = event.target instanceof Element ? event.target.closest("a[href]") : null;
    // Contextual links call requestNavigation themselves before opening their panel.
    if (target?.hasAttribute("data-workspace-context-link")) return;
    if (!target || target.getAttribute("target") || target.hasAttribute("download")) return;
    if (target.getAttribute("href")?.startsWith("#")) return;
    const url = new URL(target.getAttribute("href")!, window.location.origin);
    if (url.origin !== window.location.origin) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
    if (!guards.current.size) return;
    event.preventDefault();
    event.stopPropagation();
    requestNavigation(() => navigate(url.pathname + url.search + url.hash));
  }}>{children}</div></WorkspaceNavigationContext.Provider>;
}
